/**
 * ExternalRelease form component for creating and editing ExternalRelease records.
 * Only includes basic info + Momenta release files (no supplier files).
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Form, Input, Button, Space, Divider, message, Card, Radio, Select, Tooltip } from 'antd';
import { SaveOutlined, CloseOutlined, DeleteOutlined, CheckSquareOutlined, QuestionCircleOutlined, PlusOutlined } from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload';
import { MultiFileUpload } from '@/components/common/MultiFileUpload';
import { getExternalReleaseProjectFiles, deleteExternalReleaseFile, failExternalReleaseNoteUpload, failExternalReleaseTestReportUpload } from '@/api/externalRelease';
import { createExternalRelease, updateExternalRelease, patchExternalRelease, updateExternalReleaseUploadStatus, completeExternalReleaseFileUpload, failExternalReleaseFileUpload, startExternalReleaseFileUpload, heartbeatExternalReleaseFile } from '@/api/externalRelease';
import { updateUploadRecord } from '@/api/uploadRecords';
import type { UploadedFileItem } from '@/api/uploadRecords';
import { reportExternalReleaseUploadRecord } from '@/utils/uploadRecordUtils';
import { toVehicleList } from '@/utils/vehicle';
import { listPackages } from '@/api/packages';
import { uploadFile } from '@/services/obsService';
import { useUploadProgressStore } from '@/store/uploadProgressStore';
import type { ExternalReleaseDetail, ExternalReleaseVersionType, Project, FileType, ProjectFile } from '@/types';
import { VERSION_TYPE_OPTIONS, VERSION_TYPE_LABELS } from '@/constants/externalRelease';

const { TextArea } = Input;

interface UploadedFile {
  objectKey: string;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  md5?: string;
}

interface UploadedFileWithType extends UploadedFile {
  fileTypeId: number;
  fileId?: number;
  sourceType?: 'upload' | 'select' | 'git' | 'external';  // external：RPA 外链，只能原样保留或删除
  remark?: string;  // 文件级备注（UI 层字段，提交时映射为 comment）
  rawFile?: RcFile;  // present for deferred (not-yet-uploaded) files
  uploadKey?: string;  // progress store key, e.g. "externalrelease-1-file-23"
  uploadStatus?: 'queued' | 'uploading' | 'done' | 'failed';
}

/** 提交时保留 select / external，其余（含新上传）一律 upload */
const keptSourceType = (f: UploadedFileWithType): 'upload' | 'select' | 'external' =>
  f.sourceType === 'select' || f.sourceType === 'external' ? f.sourceType : 'upload';

interface ExternalReleaseFormProps {
  currentProject: Project | undefined;
  initialData?: ExternalReleaseDetail | null;
  isEdit: boolean;
  onSuccess: (releaseId?: number, hasDeferred?: boolean) => void;
  onCancel: () => void;
  erId?: number;
}

export const ExternalReleaseForm: React.FC<ExternalReleaseFormProps> = ({
  currentProject,
  initialData,
  isEdit,
  onSuccess,
  onCancel,
  erId: _erId,
}) => {
  const [form] = Form.useForm();
  const { setProgress, incrementAutoRetry } = useUploadProgressStore();

  // Momenta fixed files state
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, UploadedFileWithType | UploadedFileWithType[] | undefined>>({});
  const [firmwareModes, setFirmwareModes] = useState<Record<string, 'upload' | 'git' | 'artifact'>>({});
  const [gitInfo, setGitInfo] = useState<Record<string, { url: string; branch: string; commit: string; tag: string }>>({});
  const [artifactInfo, setArtifactInfo] = useState<Record<string, string[]>>({});
  const [remarkInfo, setRemarkInfo] = useState<Record<string, string>>({});
  const [pendingDeleteFiles, setPendingDeleteFiles] = useState<Map<string, UploadedFileWithType[]>>(new Map());

  // Momenta docs state
  const [momentaReleaseNoteMode, setMomentaReleaseNoteMode] = useState<'upload' | 'input'>('upload');
  const [momentaTestReportMode, setMomentaTestReportMode] = useState<'upload' | 'input'>('upload');
  const [releaseNoteFile, setReleaseNoteFile] = useState<UploadedFileWithType | undefined>(undefined);
  const [testReportFiles, setTestReportFiles] = useState<UploadedFileWithType[]>([]);

  // Existing docs in edit mode (for deferred delete)
  const [existingReleaseNote, setExistingReleaseNote] = useState<{ url: string; name: string; size: number } | null>(null);
  const [pendingDeleteReleaseNote, setPendingDeleteReleaseNote] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [, forceUpdate] = useState(0);
  const [projectFilesCache, setProjectFilesCache] = useState<Record<string, ProjectFile[]>>({});
  const [loadingFiles, setLoadingFiles] = useState<Record<string, boolean>>({});
  const [availablePackages, setAvailablePackages] = useState<{ id: number; name: string }[]>([]);
  const uploadStartedAtRef = React.useRef<string>(new Date().toISOString());
  // 编辑模式初始快照，用于判断是否有变更
  const initialSnapshot = React.useRef<{
    uploadedFiles: Record<string, UploadedFileWithType | UploadedFileWithType[] | undefined>;
    firmwareModes: Record<string, string>;
    gitInfo: Record<string, { url: string; branch: string; commit: string; tag: string }>;
    artifactInfo: Record<string, string[]>;
    remarkInfo: Record<string, string>;
    existingReleaseNote: { url: string; name: string; size: number } | null;
    momentaReleaseNoteMode: 'upload' | 'input';
    momentaTestReportMode: 'upload' | 'input';
    momentaReleaseNoteContent: string;
    momentaTestReportContent: string;
    packageIds: number[];
    name: string;
    vehicleModel: string[];
    vehicleSubtype: string[];
    platform: string;
    description: string;
    versionType: ExternalReleaseVersionType | null;
  } | null>(null);
  // 编辑前已存在的 artifact 行，提交时对比出被删除/改名的旧行，显式传给后端删除
  const initialArtifactFiles = React.useRef<Record<string, { id: number; name: string }[]>>({});

  const momentaFileTypes: FileType[] = currentProject?.momenta_file_types || [];

  const vehicleModelsWatch = Form.useWatch('vehicle_model', form) as string[] | undefined;

  // 车型/子车型 候选来自当前项目已配置的组合（`GET /projects/` 已带回 `vehicle_types`），
  // 与「系统配置 → 山头管理」同源。硬件平台是项目级单值。
  // 编辑历史数据时存量值可能不在配置里，一并补进来，保证标签可读、保存不丢值。
  const vehicleTypeOptions = useMemo(() => {
    const names = new Set<string>();
    (currentProject?.vehicle_types ?? []).forEach((vt) => {
      if (vt.vehicle_type) names.add(vt.vehicle_type);
    });
    toVehicleList(initialData?.vehicle_model).forEach((v) => names.add(v));
    return [...names].map((v) => ({ value: v, label: v }));
  }, [currentProject, initialData?.vehicle_model]);

  const vehicleSubtypeOptions = useMemo(() => {
    const names = new Set<string>();
    const selected = new Set(vehicleModelsWatch ?? []);
    (currentProject?.vehicle_types ?? [])
      .filter((vt) => selected.has(vt.vehicle_type))
      .forEach((vt) => {
        if (vt.vehicle_subtype) names.add(vt.vehicle_subtype);
      });
    toVehicleList(initialData?.vehicle_subtype).forEach((v) => names.add(v));
    return [...names].map((v) => ({ value: v, label: v }));
  }, [currentProject, vehicleModelsWatch, initialData?.vehicle_subtype]);

  const platformOptions = useMemo(() => {
    const names = new Set<string>();
    if (currentProject?.platform) names.add(currentProject.platform);
    if (initialData?.platform) names.add(initialData.platform);
    return [...names].map((v) => ({ value: v, label: v }));
  }, [currentProject?.platform, initialData?.platform]);

  // 历史 official 行仍需显示「正式版本」标签，但不可重新选中
  const versionTypeOptions = initialData?.version_type === 'official'
    ? [...VERSION_TYPE_OPTIONS, { value: 'official', label: VERSION_TYPE_LABELS.official, disabled: true }]
    : VERSION_TYPE_OPTIONS;

  const isMultiFileType = useCallback((ft: FileType) => ft.allow_multiple_files === true, []);

  // Artifact 多行输入：多文件类型可增删行，单文件类型固定一行
  const setArtifactRow = useCallback((code: string, index: number, value: string) => {
    setArtifactInfo((prev) => {
      const rows = [...(prev[code] || [''])];
      rows[index] = value;
      return { ...prev, [code]: rows };
    });
  }, []);

  const addArtifactRow = useCallback((code: string) => {
    setArtifactInfo((prev) => ({ ...prev, [code]: [...(prev[code] || ['']), ''] }));
  }, []);

  const removeArtifactRow = useCallback((code: string, index: number) => {
    setArtifactInfo((prev) => {
      const rows = [...(prev[code] || [''])];
      rows.splice(index, 1);
      return { ...prev, [code]: rows.length > 0 ? rows : [''] };
    });
  }, []);

  const fetchFilesForType = useCallback(async (fileTypeCode: string, fileTypeId: number) => {
    if (loadingFiles[fileTypeCode] || !currentProject?.id) return;
    setLoadingFiles((prev) => ({ ...prev, [fileTypeCode]: true }));
    try {
      const files = await getExternalReleaseProjectFiles(
        currentProject.id,
        fileTypeId,
        initialData?.id
      );
      setProjectFilesCache((prev) => ({ ...prev, [fileTypeCode]: files }));
    } catch {
      message.error('获取已有文件列表失败');
    } finally {
      setLoadingFiles((prev) => ({ ...prev, [fileTypeCode]: false }));
    }
  }, [currentProject?.id, initialData?.id, loadingFiles]);

  // Load available packages for this project
  useEffect(() => {
    if (!currentProject?.id) return;
    listPackages({ project_id: currentProject.id, page_size: 200 })
      .then((res) => setAvailablePackages(res.results.map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => {});
  }, [currentProject?.id]);

  // Populate form from initialData in edit mode
  useEffect(() => {
    if (!initialData) return;

    const formValues: Record<string, unknown> = {
      name: initialData.name,
      vehicle_model: toVehicleList(initialData.vehicle_model),
      vehicle_subtype: toVehicleList(initialData.vehicle_subtype),
      platform: initialData.platform || '',
      description: initialData.description,
      version_type: initialData.version_type ?? undefined,
      package_ids: initialData.packages?.map((p) => p.id) ?? [],
    };

    const newUploadedFiles: Record<string, UploadedFileWithType | UploadedFileWithType[]> = {};
    const newModes: Record<string, 'upload' | 'git' | 'artifact'> = {};
    const newGitInfo: Record<string, { url: string; branch: string; commit: string; tag: string }> = {};
    const newArtifactInfo: Record<string, string[]> = {};
    initialArtifactFiles.current = {};
    const newRemarkInfo: Record<string, string> = {};

    // Restore momenta fixed files
    initialData.files?.forEach((file) => {
      if (!file.file_type?.code) return;
      const stateKey = file.file_type.code;
      const sourceType = file.source_type || 'upload';

      if (sourceType === 'git') {
        newModes[stateKey] = 'git';
        newGitInfo[stateKey] = {
          url: file.git_url || '',
          branch: file.git_branch || '',
          commit: file.git_commit || '',
          tag: file.git_tag || '',
        };
        newRemarkInfo[stateKey] = file.comment || '';
      } else if (sourceType === 'artifact') {
        newModes[stateKey] = 'artifact';
        (initialArtifactFiles.current[stateKey] ||= []).push({ id: file.id, name: file.artifact_name || '' });
        const names = newArtifactInfo[stateKey] || (newArtifactInfo[stateKey] = []);
        const name = file.artifact_name || '';
        if (isMultiFileType(file.file_type)) {
          if (name && !names.includes(name)) names.push(name);
        } else if (names.length === 0) {
          names.push(name);
        }
        newRemarkInfo[stateKey] = file.comment || '';
      } else {
        // select / external 都按 upload 模式展示；sourceType 原样保留，提交时回传
        newModes[stateKey] = 'upload';
        const uploadedFile: UploadedFileWithType = {
          objectKey: file.object_key || '',
          fileUrl: file.file_url,
          fileName: file.file_name,
          fileSize: file.file_size,
          fileTypeId: file.file_type.id,
          fileId: file.id,
          md5: file.checksum || '',
          remark: file.comment || '',
          sourceType: sourceType as 'upload' | 'select' | 'external',
          uploadKey: `externalrelease-${initialData.id}-file-${file.id}`,
          uploadStatus: (file.upload_status as UploadedFileWithType['uploadStatus']) || 'done',
        };

        const ftConfig = momentaFileTypes.find((ft) => ft.code === file.file_type.code);
        if (ftConfig && isMultiFileType(ftConfig)) {
          if (!newUploadedFiles[stateKey]) {
            newUploadedFiles[stateKey] = [];
          }
          (newUploadedFiles[stateKey] as UploadedFileWithType[]).push(uploadedFile);
        } else {
          newUploadedFiles[stateKey] = uploadedFile;
        }
      }
    });

    setUploadedFiles(newUploadedFiles);
    setFirmwareModes(newModes);
    setGitInfo(newGitInfo);
    setArtifactInfo(newArtifactInfo);
    setRemarkInfo(newRemarkInfo);

    // Restore momenta release note: store 活跃进度优先，再看 initialData URL
    if (initialData.id) {
      const rnKey = `externalrelease-${initialData.id}-doc-release_note`;
      const storeEntry = useUploadProgressStore.getState().progress[rnKey];
      console.log('[ExternalReleaseForm] init release_note storeEntry:', storeEntry, 'key:', rnKey);
      if (storeEntry && storeEntry.status !== 'done' && storeEntry.status !== 'failed') {
        setReleaseNoteFile({
          objectKey: '',
          fileUrl: '',
          fileName: storeEntry.rawFile?.name || 'Momenta Release Note',
          fileSize: storeEntry.rawFile?.size || 0,
          fileTypeId: 0,
          fileId: -1,
          sourceType: 'upload' as const,
          rawFile: storeEntry.rawFile as RcFile,
          uploadKey: rnKey,
        });
      } else if (initialData.momenta_release_note_url) {
        setExistingReleaseNote({
          url: initialData.momenta_release_note_url,
          name: initialData.momenta_release_note_name || '',
          size: initialData.momenta_release_note_size || 0,
        });
        setMomentaReleaseNoteMode('upload');
      } else if (initialData.momenta_release_note_name && !initialData.momenta_release_note_url) {
        // 刷新后上传中断的失败状态，保留 name 供显示
        if (initialData.momenta_release_note_content) {
          formValues.momenta_release_note_content = initialData.momenta_release_note_content;
        }
      }
    }
    if (initialData.momenta_release_note_content) {
      formValues.momenta_release_note_content = initialData.momenta_release_note_content;
      if (!initialData.momenta_release_note_url) {
        setMomentaReleaseNoteMode('input');
      }
    }

    // Restore momenta test reports (multi-file list; legacy single scalar becomes one entry)
    const trEntries = initialData.momenta_test_report_files?.length
      ? initialData.momenta_test_report_files
      : (initialData.momenta_test_report_url || initialData.momenta_test_report_name)
        ? [{
            url: initialData.momenta_test_report_url || '',
            name: initialData.momenta_test_report_name || '',
            size: initialData.momenta_test_report_size || 0,
            checksum: initialData.momenta_test_report_checksum || '',
          }]
        : [];
    const trFiles: UploadedFileWithType[] = trEntries.map((entry) => ({
      objectKey: '',
      fileUrl: entry.url || '',
      fileName: entry.name || '',
      fileSize: entry.size || 0,
      md5: entry.checksum || '',
      fileTypeId: 0,
      fileId: -1,
      sourceType: 'upload' as const,
      uploadStatus: !entry.url && initialData.momenta_test_report_upload_status === 'failed'
        ? 'failed' as const
        : undefined,
    }));
    if (initialData.id) {
      const progress = useUploadProgressStore.getState().progress;
      const prefix = `externalrelease-${initialData.id}-doc-test_report`;
      Object.entries(progress).forEach(([key, entry]) => {
        if (!entry || entry.status === 'done') return;
        if (key !== prefix && !key.startsWith(`${prefix}-`)) return;
        const restored: UploadedFileWithType = {
          objectKey: '',
          fileUrl: '',
          fileName: entry.rawFile?.name || 'Momenta 测试报告',
          fileSize: entry.rawFile?.size || 0,
          fileTypeId: 0,
          fileId: -1,
          sourceType: 'upload' as const,
          rawFile: entry.rawFile as RcFile,
          uploadKey: key,
          uploadStatus: entry.status === 'failed' ? 'failed' : undefined,
        };
        const index = Number(key.slice(prefix.length + 1));
        const pending = key !== prefix && Number.isInteger(index) ? trFiles[index] : undefined;
        if (pending && !pending.fileUrl) {
          trFiles[index] = { ...pending, ...restored };
        } else {
          trFiles.push(restored);
        }
      });
    }
    setTestReportFiles(trFiles);
    if (trFiles.some((f) => f.fileUrl)) {
      setMomentaTestReportMode('upload');
    }
    if (initialData.momenta_test_report_content) {
      formValues.momenta_test_report_content = initialData.momenta_test_report_content;
      if (trFiles.length === 0) {
        setMomentaTestReportMode('input');
      }
    }

    form.setFieldsValue(formValues);

    // 记录初始快照，用于判断编辑时是否有变更（只记录一次）
    const initRnMode: 'upload' | 'input' = (initialData.momenta_release_note_content && !initialData.momenta_release_note_url) ? 'input' : 'upload';
    const initTrMode: 'upload' | 'input' = (initialData.momenta_test_report_content
      && !initialData.momenta_test_report_url
      && !initialData.momenta_test_report_files?.length) ? 'input' : 'upload';
    initialSnapshot.current = {
      uploadedFiles: { ...newUploadedFiles },
      firmwareModes: { ...newModes },
      gitInfo: { ...newGitInfo },
      artifactInfo: Object.fromEntries(Object.entries(newArtifactInfo).map(([k, v]) => [k, [...v]])),
      remarkInfo: { ...newRemarkInfo },
      existingReleaseNote: initialData.momenta_release_note_url
        ? { url: initialData.momenta_release_note_url, name: initialData.momenta_release_note_name || '', size: initialData.momenta_release_note_size || 0 }
        : null,
      momentaReleaseNoteMode: initRnMode,
      momentaTestReportMode: initTrMode,
      momentaReleaseNoteContent: (initialData.momenta_release_note_content || '') as string,
      momentaTestReportContent: (initialData.momenta_test_report_content || '') as string,
      packageIds: initialData.packages?.map((p) => p.id) ?? [],
      name: (initialData.name || '') as string,
      vehicleModel: toVehicleList(initialData.vehicle_model),
      vehicleSubtype: toVehicleList(initialData.vehicle_subtype),
      platform: (initialData.platform || '') as string,
      description: (initialData.description || '') as string,
      versionType: initialData.version_type ?? null,
    };
  }, [initialData, form, isMultiFileType]); // eslint-disable-line react-hooks/exhaustive-deps

  // 编辑界面对失败文件的重试：直接上传，不需要重新提交表单
  const handleFormRetry = async (file: UploadedFileWithType, rawFile: File) => {
    if (!initialData?.id || !file.fileId || !file.uploadKey) return;
    const erId = initialData.id;
    const fileId = file.fileId;
    const key = file.uploadKey;
    setProgress(key, { percent: 0, status: 'uploading', rawFile });
    // 心跳：刷新 updated_at，防止其他 Tab 把正在进行的重传误判为卡住
    let currentPercent = 0;
    const heartbeatTimer = setInterval(() => {
      heartbeatExternalReleaseFile(erId, fileId, currentPercent).catch(() => {});
    }, 1 * 60 * 1000);
    try {
      const result = await uploadFile(rawFile, (p) => {
        setProgress(key, { percent: p.percent, currentSpeed: p.currentSpeed, status: 'uploading', rawFile });
        currentPercent = p.percent;
      }, undefined, () => { incrementAutoRetry(key); }, key);
      if (!result.md5) {
        console.error(`[ExternalReleaseForm] MD5 empty for ${rawFile.name}, marking as failed`);
        setProgress(key, { percent: 0, status: 'failed', rawFile });
        await failExternalReleaseFileUpload(initialData.id, file.fileId).catch(() => {});
        return;
      }
      await completeExternalReleaseFileUpload(initialData.id, file.fileId, {
        file_url: result.fileUrl,
        object_key: result.objectKey,
        file_size: result.fileSize,
        checksum: result.md5,
        upload_started_at: useUploadProgressStore.getState().getProgress(key)?.uploadStartedAt ?? null,
        upload_finished_at: new Date().toISOString(),
      });
      setProgress(key, { percent: 100, status: 'done' });
      setUploadedFiles(prev => {
        const stateKey = Object.keys(prev).find(k => {
          const entry = prev[k];
          if (Array.isArray(entry)) {
            return entry.some(f => (f as UploadedFileWithType).uploadKey === key);
          }
          return (entry as UploadedFileWithType)?.uploadKey === key;
        });
        if (!stateKey) return prev;
        const entry = prev[stateKey];
        const patch = { md5: result.md5 || '', fileUrl: result.fileUrl, objectKey: result.objectKey, fileSize: result.fileSize, fileName: rawFile.name };
        if (Array.isArray(entry)) {
          return { ...prev, [stateKey]: entry.map(f => (f as UploadedFileWithType).uploadKey === key ? { ...f, ...patch } : f) };
        }
        return { ...prev, [stateKey]: { ...entry, ...patch } as UploadedFileWithType };
      });
    } catch {
      // 若 progress store 已被删除操作清除，说明是删除导致的中止，跳过 fail 标记
      if (!useUploadProgressStore.getState().progress[key]) return;
      setProgress(key, { percent: 0, status: 'failed', rawFile });
      await failExternalReleaseFileUpload(initialData.id, file.fileId).catch(() => {});
    } finally {
      clearInterval(heartbeatTimer);
    }
  };

  const handleClearGit = (stateKey: string) => {
    setGitInfo((prev) => {
      const updated = { ...prev };
      delete updated[stateKey];
      return updated;
    });
    setRemarkInfo((prev) => {
      const updated = { ...prev };
      delete updated[stateKey];
      return updated;
    });
    setFirmwareModes((prev) => ({ ...prev, [stateKey]: 'upload' }));
  };

  const handleDeleteReleaseNote = async () => {
    setPendingDeleteReleaseNote(prev => !prev);
  };

  const handleSubmit = async (values: Record<string, unknown>) => {
    // Validate git fields: all-or-nothing
    for (const ft of momentaFileTypes) {
      const stateKey = ft.code;
      const mode = firmwareModes[stateKey] || 'upload';
      if (mode === 'git') {
        const git = gitInfo[stateKey];
        const hasAny = git && (git.url || git.branch || git.commit);
        const hasAll = git && git.url && git.branch && git.commit;
        if (hasAny && !hasAll) {
          message.warning({
            content: `${ft.name} 的Git信息填写不完整。请要么填写完整（Git URL、分支、Commit ID），要么全部清空。`,
            duration: 5,
          });
          return;
        }
      }
    }

    // Artifact 同一文件类型内不允许重复产物包名
    for (const ft of momentaFileTypes) {
      const stateKey = ft.code;
      if ((firmwareModes[stateKey] || 'upload') !== 'artifact') continue;
      const names = (artifactInfo[stateKey] || []).map((n) => n.trim()).filter(Boolean);
      const dup = names.filter((n, i) => names.indexOf(n) !== i);
      if (dup.length > 0) {
        message.error(`${ft.name} 中存在重复的 Artifact 产物包名：${[...new Set(dup)].join(', ')}，请修改后再提交`);
        return;
      }
    }

    const trDupNames = testReportFiles.map((f) => f.fileName).filter(Boolean);
    const trDup = trDupNames.filter((n, i) => trDupNames.indexOf(n) !== i);
    if (trDup.length > 0) {
      message.error(`Momenta 测试报告中存在同名文件：${[...new Set(trDup)].join(', ')}，请删除重复文件后再提交`);
      return;
    }

    setSubmitting(true);

    try {
      // Snapshot pending deletes BEFORE building the payload.
      // Pass them to the PUT request so the backend deletes + logs in one operation.
      const capturedPendingDeletes = new Map(pendingDeleteFiles);
      const capturedPendingDeleteRN = pendingDeleteReleaseNote;
      const capturedPendingDeleteTR = (capturedPendingDeletes.get('momenta_test_report') || []).length > 0;
      console.log('[ExternalReleaseForm:submit] capturedPendingDeletes=', [...capturedPendingDeletes.entries()].map(([k, v]) => ({ typeCode: k, files: v.map(f => ({ fileId: f.fileId, fileName: f.fileName })) })), 'pendingRN=', capturedPendingDeleteRN, 'pendingTR=', capturedPendingDeleteTR);
      setPendingDeleteFiles(new Map());

      // Build files array
      const files: Array<{
        file_type_id: number;
        file_name: string;
        file_url?: string;
        file_size: number;
        object_key?: string;
        checksum?: string;
        source_type?: 'upload' | 'select' | 'git' | 'artifact' | 'external';
        git_url?: string;
        git_branch?: string;
        git_commit?: string;
        git_tag?: string;
        artifact_name?: string;
        upload_status?: string;
        comment?: string;
        _rawFile?: RcFile;
      }> = [];

      for (const ft of momentaFileTypes) {
        const stateKey = ft.code;
        const mode = firmwareModes[stateKey] || 'upload';

        if (mode === 'git') {
          const git = gitInfo[stateKey];
          if (!git || !git.url || !git.branch || !git.commit) continue;
          files.push({
            file_type_id: ft.id,
            file_name: `Git-${git.url.split('/').pop()}-${git.branch}-${git.commit.substring(0, 6)}`,
            file_size: 0,
            source_type: 'git',
            git_url: git.url,
            git_branch: git.branch,
            git_commit: git.commit,
            git_tag: git.tag || '',
            comment: remarkInfo[stateKey] ?? '',
          });
        } else if (mode === 'artifact') {
          const names = (artifactInfo[stateKey] || []).map((n) => n.trim()).filter(Boolean);
          for (const name of names) {
            files.push({
              file_type_id: ft.id,
              file_name: name,
              file_size: 0,
              source_type: 'artifact',
              artifact_name: name,
              comment: remarkInfo[stateKey] ?? '',
            });
          }
        } else {
          const uploadData = uploadedFiles[stateKey];
          if (!uploadData) continue;

          // Filter out files that were marked for deferred deletion
          const deletedFileIds = new Set(
            (capturedPendingDeletes.get(stateKey) || [])
              .map(f => f.fileId).filter((id): id is number => id != null)
          );

          if (isMultiFileType(ft)) {
            const rawArr = uploadData as UploadedFileWithType[];
            const arr = deletedFileIds.size > 0
              ? rawArr.filter(f => !f.fileId || !deletedFileIds.has(f.fileId))
              : rawArr;
            for (const f of arr) {
              const isDeferred = !!f.rawFile;
              files.push({
                file_type_id: ft.id,
                file_name: f.fileName,
                file_url: isDeferred ? '' : f.fileUrl,
                file_size: f.fileSize,
                object_key: isDeferred ? '' : (f.objectKey || ''),
                checksum: isDeferred ? '' : (f.md5 || ''),
                source_type: keptSourceType(f),
                upload_status: isDeferred ? 'queued' : 'done',
                comment: f.remark ?? '',
                _rawFile: isDeferred ? f.rawFile : undefined,
              });
            }
          } else {
            const f = uploadData as UploadedFileWithType;
            // Skip if this single file was marked for deferred deletion
            if (f.fileId && deletedFileIds.has(f.fileId)) {
              console.log('[ExternalReleaseForm:submit] single file deleted, skipping from payload: typeCode=', stateKey, 'fileId=', f.fileId);
              continue;
            }
            const isDeferred = !!f.rawFile;
            files.push({
              file_type_id: ft.id,
              file_name: f.fileName,
              file_url: isDeferred ? '' : f.fileUrl,
              file_size: f.fileSize,
              object_key: isDeferred ? '' : (f.objectKey || ''),
              checksum: isDeferred ? '' : (f.md5 || ''),
              source_type: keptSourceType(f),
              upload_status: isDeferred ? 'queued' : 'done',
              comment: f.remark ?? '',
              _rawFile: isDeferred ? f.rawFile : undefined,
            });
          }
        }
      }

      const payload: Record<string, unknown> = {
        name: values.name,
        vehicle_model: (values.vehicle_model as string[] | undefined) ?? [],
        vehicle_subtype: (values.vehicle_subtype as string[] | undefined) ?? [],
        platform: values.platform || '',
        description: values.description || '',
        version_type: (values.version_type as ExternalReleaseVersionType | undefined) ?? null,
        files,
        package_ids: (values.package_ids as number[]) ?? [],
      };

      // Momenta release note
      // 文字内容以表单当前值为准：用户清空就是清空，不能回填 initialData 的旧值
      const rnText = (form.getFieldValue('momenta_release_note_content') as string) || '';
      const rnDeferred = !!(releaseNoteFile?.rawFile && !releaseNoteFile?.fileUrl);
      if (rnDeferred && releaseNoteFile) {
        payload.momenta_release_note_name = releaseNoteFile.fileName || '';
        payload.momenta_release_note_size = releaseNoteFile.fileSize || 0;
      } else if (releaseNoteFile?.fileUrl) {
        payload.momenta_release_note_url = releaseNoteFile.fileUrl;
        payload.momenta_release_note_name = releaseNoteFile.fileName || '';
        payload.momenta_release_note_size = releaseNoteFile.fileSize || 0;
      } else if (isEdit && existingReleaseNote?.url && !pendingDeleteReleaseNote) {
        // 保留已有文件（未标记待删除）
        payload.momenta_release_note_url = existingReleaseNote.url;
        payload.momenta_release_note_name = existingReleaseNote.name;
        payload.momenta_release_note_size = existingReleaseNote.size;
      }
      payload.momenta_release_note_content = rnText;

      // Momenta test reports (authoritative list; deletions = omission from the list)
      const trText = (form.getFieldValue('momenta_test_report_content') as string) || '';
      const trPendingDelete = capturedPendingDeletes.get('momenta_test_report') || [];
      const trDeletedKeys = new Set(trPendingDelete.map(f => f.fileUrl || f.fileName));
      // 以 DB 现有列表为底（避免取消删除标记时丢文件），去掉待删除项，再补新增项
      const savedTrDocs = initialData?.momenta_test_report_files?.length
        ? initialData.momenta_test_report_files
        : (initialData?.momenta_test_report_url || initialData?.momenta_test_report_name)
          ? [{
              url: initialData.momenta_test_report_url || '',
              name: initialData.momenta_test_report_name || '',
              size: initialData.momenta_test_report_size || 0,
              checksum: initialData.momenta_test_report_checksum || '',
            }]
          : [];
      const trStateByKey = new Map(testReportFiles.map(f => [f.fileUrl || f.fileName, f]));
      const trSavedKeys = new Set(savedTrDocs.map(d => d.url || d.name));
      const trEffective: UploadedFileWithType[] = [];
      savedTrDocs.forEach((d) => {
        const key = d.url || d.name;
        if (!key || trDeletedKeys.has(key)) return;
        trEffective.push(trStateByKey.get(key) ?? {
          objectKey: '',
          fileUrl: d.url || '',
          fileName: d.name || '',
          fileSize: d.size || 0,
          md5: d.checksum || '',
          fileTypeId: 0,
          fileId: -1,
          sourceType: 'upload' as const,
        });
      });
      testReportFiles.forEach((f) => {
        const key = f.fileUrl || f.fileName;
        if (!trSavedKeys.has(key) && !trDeletedKeys.has(key)) trEffective.push(f);
      });
      const trDeferred = trEffective.some(f => !!f.rawFile && !f.fileUrl);
      payload.momenta_test_report_files = trEffective.map((f) => ({
        url: f.fileUrl || '',
        name: f.fileName || '',
        size: f.fileSize || 0,
        checksum: f.md5 || '',
      }));
      payload.momenta_test_report_content = trText;

      if (isEdit && initialData?.version != null) {
        payload.version = initialData.version;
      }

      const deferredFixedFiles = (files as Array<Record<string, unknown>>).filter(f => (f.upload_status === 'queued' || f.upload_status === 'uploading') && f._rawFile);
      const hasAnyDeferred = deferredFixedFiles.length > 0 || rnDeferred || trDeferred;

      // Strip non-API fields before sending
      const apiFiles = (files as Array<Record<string, unknown>>).map(({ _rawFile: _r, ...rest }) => rest);
      const apiPayload: Record<string, unknown> = { ...payload, files: apiFiles };

      // Attach deferred deletions to the PUT payload so backend merges into one log entry.
      // Artifact 行在 multi upsert 里按名字匹配，被删除/改名的旧行必须显式带上 id。
      if (isEdit) {
        const deletedFileIds: number[] = [];
        for (const [typeCode, filesToDelete] of capturedPendingDeletes) {
          if (typeCode === 'momenta_test_report') continue;
          for (const file of filesToDelete) {
            if (file.fileId) deletedFileIds.push(file.fileId);
          }
        }
        for (const [code, olds] of Object.entries(initialArtifactFiles.current)) {
          const mode = firmwareModes[code] || 'upload';
          const currentNames = new Set(
            mode === 'artifact' ? (artifactInfo[code] || []).map((n) => n.trim()).filter(Boolean) : [],
          );
          for (const old of olds) {
            if (!currentNames.has(old.name.trim())) deletedFileIds.push(old.id);
          }
        }
        if (deletedFileIds.length > 0) apiPayload.deleted_file_ids = deletedFileIds;
        if (capturedPendingDeleteRN) apiPayload.pending_delete_release_note = true;
        // 测试报告改为整表提交，删除即从 momenta_test_report_files 中移除
      }

      let result: { id: number; files?: Array<{ id: number; file_type_id?: number; file_type?: { id: number }; file_name?: string }>; version?: number } | null = null;

      if (isEdit && initialData?.id) {
        result = await updateExternalRelease(initialData.id, apiPayload as Parameters<typeof updateExternalRelease>[1]);
      } else {
        if (!currentProject?.id) {
          message.error('未找到项目信息');
          return;
        }
        result = await createExternalRelease({ ...apiPayload, project_id: currentProject.id } as Parameters<typeof createExternalRelease>[0]);
      }

      message.success(isEdit ? '更新成功，文件正在后台上传中...' : '创建成功，文件正在后台上传中...');
      onSuccess(result?.id, hasAnyDeferred);

      // Report upload record (awaited so uploadRecordId is available for the no-deferred branch)
      let uploadRecordId: number | null = null;
      if (result?.id && currentProject?.id) {
        const recordResult = await reportExternalReleaseUploadRecord(
          result as ExternalReleaseDetail,
          initialData,
          currentProject.id,
          {
            name: (values.name as string) || '',
            vehicleModel: toVehicleList(values.vehicle_model as string[] | undefined)[0] ?? '',
            description: (values.description as string) || '',
          },
          uploadStartedAtRef.current,
        );
        if (recordResult?.recordId) uploadRecordId = recordResult.recordId;
      }

      // Background uploads
      if (hasAnyDeferred && result?.id) {
        const erId = result.id;
        const createdFiles = result.files || [];

        // Mark as uploading FIRST (awaited) so the status is correct before uploads start
        console.log('[ExternalReleaseForm] 调用 updateExternalReleaseUploadStatus uploading, erId=', erId);
        await updateExternalReleaseUploadStatus(erId, 'uploading').catch(() => {});
        console.log('[ExternalReleaseForm] updateExternalReleaseUploadStatus uploading 完成');

        // Track failures so we can set the correct final status
        const failures: string[] = [];

        // Accumulate doc upload timing client-side so multiple doc uploads don't overwrite each other
        const currentDocTiming: Record<string, { started_at: string; finished_at: string; avg_speed_mbps?: number | null }> = {
          ...(initialData?.doc_upload_timing ?? {}),
        };

        // Semaphore: at most 1 file uploads at a time, others queue
        const MAX_CONCURRENT = 1;
        let semCount = 0;
        const semQueue: (() => void)[] = [];
        const semAcquire = () => new Promise<void>(resolve => {
          if (semCount < MAX_CONCURRENT) { semCount++; resolve(); }
          else semQueue.push(resolve);
        });
        const semRelease = () => {
          semCount--;
          const next = semQueue.shift();
          if (next) { semCount++; next(); }
        };

        const allUploads: Promise<void>[] = [];
        const deferredFilesMeta: Array<{ key: string; file_name: string; file_size: number; file_type_name: string }> = [];

        // Helper to build snapshot of all files for upload record
        const buildERAllFilesSnapshot = (erId: number): UploadedFileItem[] => {
          const snapshot: UploadedFileItem[] = [];
          const store = useUploadProgressStore.getState();

          const mkKey = (f: any): string => {
            const st = f.source_type || 'upload';
            if (st === 'git') return `git:${f.git_url || ''}|${f.git_branch || ''}|${f.git_commit || ''}`;
            if (st === 'artifact') return `artifact:${f.artifact_name || ''}`;
            // 外链的 object_key 恒为空，按 file_url 区分
            if (st === 'external') return `external:${f.file_url || ''}`;
            const typeId = f.file_type_id ?? f.file_type?.id;
            return `${typeId}::${f.file_name ?? ''}::${f.object_key || ''}`;
          };
          const initFileKeys = new Set(
            isEdit ? (initialData?.files || []).map(mkKey) : []
          );
          const createdFileKeys = new Set(
            (createdFiles || []).map(mkKey)
          );

          // Current DB files
          (createdFiles || []).forEach((f: any) => {
            const key = `externalrelease-${erId}-file-${f.id}`;
            const e = store.getProgress(key);
            const fKey = mkKey(f);
            const isExistingFile = isEdit && initFileKeys.has(fKey);
            const effectiveStatus = e?.status ?? 'done';
            snapshot.push({
              file_name: e?.rawFile?.name ?? f.file_name ?? '',
              file_size: e?.rawFile?.size ?? f.file_size ?? 0,
              file_type_name: (f.file_type?.name || momentaFileTypes.find(ft => ft.id === (f.file_type_id ?? f.file_type?.id))?.name) || '',
              auto_retry_count: e?.autoRetryCount ?? 0,
              manual_retry_count: e?.manualRetryCount ?? 0,
              upload_started_at: e?.uploadStartedAt ?? null,
              upload_finished_at: e?.uploadFinishedAt ?? null,
              avg_speed_mbps: e?.avgSpeedMbps ?? null,
              change_type: effectiveStatus === 'failed' ? 'failed' : (isExistingFile ? 'existing' : 'added'),
            });
          });

          // Deleted firmware files: were in initialData but not in createdFiles
          if (isEdit) {
            (initialData?.files || []).forEach((f: any) => {
              const fKey = mkKey(f);
              if (!createdFileKeys.has(fKey)) {
                snapshot.push({
                  file_name: f.file_name || '',
                  file_size: f.file_size || 0,
                  file_type_name: f.file_type?.name || '',
                  auto_retry_count: 0, manual_retry_count: 0,
                  upload_started_at: null, upload_finished_at: null, avg_speed_mbps: null,
                  change_type: 'deleted',
                });
              }
            });
          }

          const initRnUrl = isEdit ? initialData?.momenta_release_note_url || '' : '';

          // Deleted docs: user explicitly clicked delete
          if (isEdit && initRnUrl && capturedPendingDeleteRN) {
            snapshot.push({
              file_name: initialData?.momenta_release_note_name || 'Momenta Release Note',
              file_size: initialData?.momenta_release_note_size || 0,
              file_type_name: 'Momenta Release Note',
              auto_retry_count: 0, manual_retry_count: 0,
              upload_started_at: null, upload_finished_at: null, avg_speed_mbps: null,
              change_type: 'deleted',
            });
          }
          const trPrefix = `externalrelease-${erId}-doc-test_report-`;
          const beforeTrDocs = initialData?.momenta_test_report_files?.length
            ? initialData.momenta_test_report_files
            : initialData?.momenta_test_report_url || initialData?.momenta_test_report_name
              ? [{
                  url: initialData.momenta_test_report_url || '',
                  name: initialData.momenta_test_report_name || '',
                  size: initialData.momenta_test_report_size || 0,
                  checksum: initialData.momenta_test_report_checksum || '',
                }]
              : [];
          if (isEdit) {
            const effectiveKeys = new Set(trEffective.map(f => f.fileUrl || f.fileName));
            beforeTrDocs.forEach((d) => {
              if (!effectiveKeys.has(d.url || d.name) && (d.url || d.name)) {
                snapshot.push({
                  file_name: d.name || 'Momenta Test Report',
                  file_size: d.size || 0, file_type_name: 'Momenta Test Report',
                  auto_retry_count: 0, manual_retry_count: 0,
                  upload_started_at: null, upload_finished_at: null, avg_speed_mbps: null,
                  change_type: 'deleted',
                });
              }
            });
          }
          trEffective.forEach((file, index) => {
            const key = `${trPrefix}${index}`;
            const e = store.getProgress(key);
            const meta = deferredFilesMeta.find(m => m.key === key);
            if (meta) {
              const effectiveStatus = e?.status ?? 'done';
              snapshot.push({
                file_name: e?.rawFile?.name ?? meta.file_name,
                file_size: e?.rawFile?.size ?? meta.file_size,
                file_type_name: 'Momenta Test Report',
                auto_retry_count: e?.autoRetryCount ?? 0,
                manual_retry_count: e?.manualRetryCount ?? 0,
                upload_started_at: e?.uploadStartedAt ?? null,
                upload_finished_at: e?.uploadFinishedAt ?? null,
                avg_speed_mbps: e?.avgSpeedMbps ?? null,
                change_type: effectiveStatus === 'failed' ? 'failed' : 'added',
              });
            } else if (file.fileUrl) {
              snapshot.push({
                file_name: file.fileName || 'Momenta Test Report',
                file_size: file.fileSize || 0, file_type_name: 'Momenta Test Report',
                auto_retry_count: 0, manual_retry_count: 0,
                upload_started_at: null, upload_finished_at: null, avg_speed_mbps: null,
                change_type: 'existing',
              });
            }
          });
          // Momenta Release Note（单文件）
          {
            const key = `externalrelease-${erId}-doc-release_note`;
            const e = store.getProgress(key);
            const meta = deferredFilesMeta.find(m => m.key === key);
            if (e && meta) {
              const effectiveStatus = e?.status ?? 'done';
              snapshot.push({
                file_name: e?.rawFile?.name ?? meta.file_name,
                file_size: e?.rawFile?.size ?? meta.file_size,
                file_type_name: 'Momenta Release Note',
                auto_retry_count: e?.autoRetryCount ?? 0,
                manual_retry_count: e?.manualRetryCount ?? 0,
                upload_started_at: e?.uploadStartedAt ?? null,
                upload_finished_at: e?.uploadFinishedAt ?? null,
                avg_speed_mbps: e?.avgSpeedMbps ?? null,
                change_type: effectiveStatus === 'failed' ? 'failed' : 'added',
              });
            } else if (isEdit && initRnUrl) {
              snapshot.push({
                file_name: initialData?.momenta_release_note_name || 'Momenta Release Note',
                file_size: initialData?.momenta_release_note_size || 0,
                file_type_name: 'Momenta Release Note',
                auto_retry_count: 0, manual_retry_count: 0,
                upload_started_at: null, upload_finished_at: null, avg_speed_mbps: null,
                change_type: 'existing',
              });
            }
          }

          return snapshot;
        };

        // Consumable list — each createdFile is spliced out once matched to prevent
        // multiple deferredFixedFiles of the same file_type_id from mapping to the same DB record.
        const unmatchedCreated = createdFiles.filter((cf: any) => cf.upload_status === 'queued' || cf.upload_status === 'uploading').slice();

        deferredFixedFiles.forEach(fileData => {
          const rawFile = fileData._rawFile as RcFile;
          // Prefer exact file_name match; fall back to file_type_id.
          let matchIdx = unmatchedCreated.findIndex((cf: any) => cf.file_name === fileData.file_name);
          if (matchIdx === -1) {
            matchIdx = unmatchedCreated.findIndex((cf: any) =>
              (cf.file_type_id !== undefined && cf.file_type_id === fileData.file_type_id) ||
              (cf.file_type?.id !== undefined && cf.file_type.id === fileData.file_type_id)
            );
          }
          if (matchIdx === -1 || !rawFile) return;
          const createdFile = unmatchedCreated.splice(matchIdx, 1)[0];

          const key = `externalrelease-${erId}-file-${createdFile.id}`;
          const fileTypeName = momentaFileTypes.find(ft => ft.id === (fileData.file_type_id as number ?? createdFile.file_type_id ?? createdFile.file_type?.id))?.name || '';
          deferredFilesMeta.push({ key, file_name: String(fileData.file_name || ''), file_size: Number(fileData.file_size) || 0, file_type_name: fileTypeName });
          // 入队即标 queued（仅本地展示）；DB 里的初始态已是 queued（见提交载荷），
          // 这里不再写库，等拿到上传槽位时再置 uploading
          setProgress(key, { percent: 0, status: 'queued' });

          allUploads.push((async () => {
            // 心跳：每 1 分钟刷新 updated_at，防止其他 Tab 将此文件误判为卡住
            // currentPercent 在进度回调中无条件更新，确保定时器上报的是实时进度而非陈旧值
            let lastHeartbeatPercent = 0;
            let currentPercent = 0;
            const heartbeatTimer = setInterval(() => {
              lastHeartbeatPercent = currentPercent;
              heartbeatExternalReleaseFile(erId, createdFile.id, currentPercent).catch(() => {});
            }, 1 * 60 * 1000);
            try {
              await semAcquire();
            } catch (err) {
              clearInterval(heartbeatTimer);
              throw err;
            }
            const startedAt = new Date().toISOString();
            // Slot acquired — switch to uploading
            setProgress(key, { percent: 0, status: 'uploading', rawFile, uploadStartedAt: startedAt, uploadFinishedAt: null, avgSpeedMbps: null });
            startExternalReleaseFileUpload(erId, createdFile.id, 'uploading').catch(() => {});
            try {
              const r = await uploadFile(rawFile, (p) => {
                setProgress(key, { percent: p.percent, currentSpeed: p.currentSpeed, status: 'uploading', rawFile });
                currentPercent = p.percent;
                // 每涨 5% 向服务端写一次进度，让其他会话可感知
                // 用差值比较而非 %10===0：进度可能跨过整十数，严格整除会导致此后永不上报
                if (p.percent > 0 && p.percent - lastHeartbeatPercent >= 5) {
                  lastHeartbeatPercent = p.percent;
                  heartbeatExternalReleaseFile(erId, createdFile.id, p.percent).catch(() => {});
                }
              }, undefined, () => { incrementAutoRetry(key); });
              if (!r.md5) {
                console.error(`[ExternalReleaseForm] MD5 empty for ${fileData.file_name}, marking as failed`);
                failures.push(String(fileData.file_name));
                setProgress(key, { percent: 0, status: 'failed', rawFile, uploadFinishedAt: new Date().toISOString() });
                await failExternalReleaseFileUpload(erId, createdFile.id).catch(() => {});
              } else {
                const finishedAt = new Date().toISOString();
                const durationSec = (new Date(finishedAt).getTime() - new Date(startedAt).getTime()) / 1000;
                const avgSpeedMbps = durationSec > 0 ? parseFloat((r.fileSize / 1024 / 1024 / durationSec).toFixed(2)) : null;
                setProgress(key, { percent: 100, status: 'done', uploadFinishedAt: finishedAt, avgSpeedMbps });
                if (uploadRecordId) {
                  const snapshot = buildERAllFilesSnapshot(erId);
                  const activeSnap = snapshot.filter(f => f.change_type !== 'deleted');
                  await updateUploadRecord(uploadRecordId, {
                    uploaded_files: snapshot,
                    file_count: activeSnap.length,
                    total_size: activeSnap.reduce((s, f) => s + (f.file_size || 0), 0),
                  }).catch(() => {});
                }
                await completeExternalReleaseFileUpload(erId, createdFile.id, {
                  file_url: r.fileUrl, object_key: r.objectKey, file_size: r.fileSize, checksum: r.md5,
                  file_name: rawFile.name,
                  upload_started_at: startedAt,
                  upload_finished_at: finishedAt,
                  avg_speed_mbps: avgSpeedMbps,
                });
              }
            } catch (err) {
              console.error(`[ExternalReleaseForm] File upload failed: ${fileData.file_name}`, err);
              failures.push(String(fileData.file_name));
              setProgress(key, { percent: 0, status: 'failed', rawFile, uploadFinishedAt: new Date().toISOString() });
              await failExternalReleaseFileUpload(erId, createdFile.id).catch(() => {});
            } finally {
              clearInterval(heartbeatTimer);
              semRelease();
            }
          })());
        });

        if (rnDeferred && releaseNoteFile?.rawFile) {
          const rnKey = `externalrelease-${erId}-doc-release_note`;
          const rnRaw = releaseNoteFile.rawFile!;
          deferredFilesMeta.push({ key: rnKey, file_name: releaseNoteFile.fileName || rnRaw.name, file_size: rnRaw.size, file_type_name: 'Momenta Release Note' });
          setProgress(rnKey, { percent: 0, status: 'queued', rawFile: rnRaw });
          allUploads.push((async () => {
            await semAcquire();
            const rnStartedAt = new Date().toISOString();
            setProgress(rnKey, { percent: 0, status: 'uploading', rawFile: rnRaw, uploadStartedAt: rnStartedAt, uploadFinishedAt: null, avgSpeedMbps: null });
            // 心跳：每 1 分钟刷新 updated_at，防止其他 Tab 将 ReleaseNote 误判为卡住
            const rnHeartbeatTimer = setInterval(() => {
              patchExternalRelease(erId, {}).catch(() => {});
            }, 1 * 60 * 1000);
            try {
              const r = await uploadFile(rnRaw, (p) => {
                setProgress(rnKey, { percent: p.percent, currentSpeed: p.currentSpeed, status: 'uploading', rawFile: rnRaw });
              }, undefined, () => { incrementAutoRetry(rnKey); });
              const rnFinishedAt = new Date().toISOString();
              const rnDurationSec = (new Date(rnFinishedAt).getTime() - new Date(rnStartedAt).getTime()) / 1000;
              const rnAvgSpeedMbps = rnDurationSec > 0 ? parseFloat((r.fileSize / 1024 / 1024 / rnDurationSec).toFixed(2)) : null;
              setProgress(rnKey, { percent: 100, status: 'done', uploadFinishedAt: rnFinishedAt, avgSpeedMbps: rnAvgSpeedMbps });
              // Flush timing to UploadRecord BEFORE telling backend the file is done,
              // so _sync_upload_record_files sees complete timing when notification fires.
              if (uploadRecordId) {
                const snapshot = buildERAllFilesSnapshot(erId);
                const activeSnap = snapshot.filter(f => f.change_type !== 'deleted');
                await updateUploadRecord(uploadRecordId, {
                  uploaded_files: snapshot,
                  file_count: activeSnap.length,
                  total_size: activeSnap.reduce((s, f) => s + (f.file_size || 0), 0),
                }).catch(() => {});
              }
              currentDocTiming['momenta_release_note'] = { started_at: rnStartedAt, finished_at: rnFinishedAt, avg_speed_mbps: rnAvgSpeedMbps };
              await updateExternalRelease(erId, {
                momenta_release_note_url: r.fileUrl,
                momenta_release_note_name: r.fileName,
                momenta_release_note_size: r.fileSize,
                momenta_release_note_checksum: r.md5 || '',
                version: result?.version || 1,
                force: true,
                skip_log: true,
                doc_upload_timing: { ...currentDocTiming },
              } as Parameters<typeof updateExternalRelease>[1]);
            } catch (err) {
              console.error('[ExternalReleaseForm] Release note upload failed', err);
              failures.push('Momenta Release Note');
              setProgress(rnKey, { percent: 0, status: 'failed', rawFile: rnRaw, uploadFinishedAt: new Date().toISOString() });
              await failExternalReleaseNoteUpload(erId).catch(() => {});
            } finally {
              clearInterval(rnHeartbeatTimer);
              semRelease();
            }
          })());
        }

        const trUploadState = trEffective.map((file) => ({ ...file }));
        let trUploadFailed = false;
        trEffective.forEach((file, index) => {
          const trRaw = file.rawFile;
          if (!trRaw || file.fileUrl) return;
          const trKey = `externalrelease-${erId}-doc-test_report-${index}`;
          deferredFilesMeta.push({ key: trKey, file_name: file.fileName || trRaw.name, file_size: trRaw.size, file_type_name: 'Momenta 测试报告' });
          setProgress(trKey, { percent: 0, status: 'queued', rawFile: trRaw });
          allUploads.push((async () => {
            await semAcquire();
            const trStartedAt = new Date().toISOString();
            setProgress(trKey, { percent: 0, status: 'uploading', rawFile: trRaw, uploadStartedAt: trStartedAt, uploadFinishedAt: null, avgSpeedMbps: null });
            // 心跳：每 1 分钟刷新 updated_at，防止其他 Tab 将 TestReport 误判为卡住
            const trHeartbeatTimer = setInterval(() => {
              patchExternalRelease(erId, {}).catch(() => {});
            }, 1 * 60 * 1000);
            try {
              const r = await uploadFile(trRaw, (p) => {
                setProgress(trKey, { percent: p.percent, currentSpeed: p.currentSpeed, status: 'uploading', rawFile: trRaw });
              }, undefined, () => { incrementAutoRetry(trKey); });
              const trFinishedAt = new Date().toISOString();
              const trDurationSec = (new Date(trFinishedAt).getTime() - new Date(trStartedAt).getTime()) / 1000;
              const trAvgSpeedMbps = trDurationSec > 0 ? parseFloat((r.fileSize / 1024 / 1024 / trDurationSec).toFixed(2)) : null;
              setProgress(trKey, { percent: 100, status: 'done', uploadFinishedAt: trFinishedAt, avgSpeedMbps: trAvgSpeedMbps });
              // Flush timing to UploadRecord BEFORE telling backend the file is done,
              // so _sync_upload_record_files sees complete timing when notification fires.
              if (uploadRecordId) {
                const snapshot = buildERAllFilesSnapshot(erId);
                const activeSnap = snapshot.filter(f => f.change_type !== 'deleted');
                await updateUploadRecord(uploadRecordId, {
                  uploaded_files: snapshot,
                  file_count: activeSnap.length,
                  total_size: activeSnap.reduce((s, f) => s + (f.file_size || 0), 0),
                }).catch(() => {});
              }
              trUploadState[index] = {
                ...trUploadState[index],
                fileUrl: r.fileUrl,
                fileName: r.fileName || trUploadState[index].fileName,
                fileSize: r.fileSize,
                md5: r.md5 || '',
                rawFile: undefined,
                uploadKey: trKey,
              };
              currentDocTiming['momenta_test_report'] = { started_at: trStartedAt, finished_at: trFinishedAt, avg_speed_mbps: trAvgSpeedMbps };
              await updateExternalRelease(erId, {
                momenta_test_report_files: trUploadState.map((f) => ({
                  url: f.fileUrl || '',
                  name: f.fileName || '',
                  size: f.fileSize || 0,
                  checksum: f.md5 || '',
                })),
                doc_files_merge: true,
                version: result?.version || 1,
                force: true,
                skip_log: true,
                doc_upload_timing: { ...currentDocTiming },
              } as Parameters<typeof updateExternalRelease>[1]);
            } catch (err) {
              console.error('[ExternalReleaseForm] Test report upload failed', err);
              trUploadFailed = true;
              setProgress(trKey, { percent: 0, status: 'failed', rawFile: trRaw, uploadFinishedAt: new Date().toISOString() });
            } finally {
              clearInterval(trHeartbeatTimer);
              semRelease();
            }
          })());
        });

        Promise.all(allUploads).then(async () => {
          if (trUploadFailed) {
            failures.push('Momenta 测试报告');
            // 成功文件的回写 PATCH 会把状态重算成「上传中」，最后再统一置 failed
            await failExternalReleaseTestReportUpload(erId).catch(() => {});
          }
          const finalStatus = failures.length > 0 ? 'failed' : 'done';
          if (uploadRecordId) {
            const allFiles = buildERAllFilesSnapshot(erId);
            const activeFiles = allFiles.filter(f => f.change_type !== 'deleted');
            await updateUploadRecord(uploadRecordId, {
              upload_status: finalStatus,
              finished_at: new Date().toISOString(),
              uploaded_files: allFiles,
              file_count: activeFiles.length,
              total_size: activeFiles.reduce((s, f) => s + (f.file_size || 0), 0),
            }).catch(() => {});
          }
          updateExternalReleaseUploadStatus(erId, finalStatus).catch(() => {});
          if (failures.length > 0) {
            message.error(`以下文件上传失败，请编辑重试：${failures.join('、')}`);
          }
        }).catch(async () => {
          if (uploadRecordId) {
            await updateUploadRecord(uploadRecordId, {
              upload_status: 'failed',
              finished_at: new Date().toISOString(),
            }).catch(() => {});
          }
          updateExternalReleaseUploadStatus(erId, 'failed').catch(() => {});
        });
      } else if (result?.id) {
        if (uploadRecordId) {
          updateUploadRecord(uploadRecordId, {
            upload_status: 'done',
            finished_at: new Date().toISOString(),
          }).catch(() => {});
        }
        updateExternalReleaseUploadStatus(result.id, 'done').catch(() => {});
      }
    } catch (error) {
      const err = error as { response?: { status: number; data?: unknown } };
      if (err.response?.status === 409) {
        message.error('数据已被其他用户修改，请刷新后重试');
      } else {
        const data = err.response?.data as Record<string, unknown> | undefined;
        let errorMsg = isEdit ? '更新失败' : '创建失败';
        if (data) {
          if (typeof data === 'string') errorMsg = data;
          else if (data.detail) errorMsg = String(data.detail);
          else if (data.error) errorMsg = String(data.error);
          else if (data.message) errorMsg = String(data.message);
          else errorMsg = JSON.stringify(data);
        }
        message.error(errorMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // 编辑模式：仅当有实际变更时才允许点击"更新"
  const hasChanges = !isEdit ? true : (() => {
    if (!initialSnapshot.current) return false;
    const snap = initialSnapshot.current;

    // firmwareModes 变化
    if (JSON.stringify(firmwareModes) !== JSON.stringify(snap.firmwareModes)) return true;

    // gitInfo 变化
    if (JSON.stringify(gitInfo) !== JSON.stringify(snap.gitInfo)) return true;

    // artifactInfo 变化
    if (JSON.stringify(artifactInfo) !== JSON.stringify(snap.artifactInfo)) return true;

    // Git/Artifact 备注变化
    if (JSON.stringify(remarkInfo) !== JSON.stringify(snap.remarkInfo)) return true;

    // pendingDeleteFiles 有内容
    if (pendingDeleteFiles.size > 0) return true;

    // 文档待删除标记
    if (pendingDeleteReleaseNote) return true;

    // 上传文件 fileId + 备注集合变化
    const fileIds = (files: Record<string, UploadedFileWithType | UploadedFileWithType[] | undefined>) =>
      Object.values(files)
        .flatMap((v) => Array.isArray(v) ? v : (v ? [v] : []))
        .map((f) => `${f.fileId != null ? String(f.fileId) : f.fileUrl}|${f.remark ?? ''}`)
        .sort()
        .join(',');
    if (fileIds(uploadedFiles) !== fileIds(snap.uploadedFiles)) return true;

    // 新文档文件（用户刚上传，尚未提交）
    if (releaseNoteFile) return true;
    const trDocsKey = (docs: Array<{ url?: string; name?: string }>) =>
      docs.map((d) => d.url || d.name || '').filter(Boolean).sort().join('|');
    const initialTrDocs = initialData?.momenta_test_report_files?.length
      ? initialData.momenta_test_report_files
      : initialData?.momenta_test_report_url || initialData?.momenta_test_report_name
        ? [{ url: initialData.momenta_test_report_url || '', name: initialData.momenta_test_report_name || '' }]
        : [];
    if (trDocsKey(testReportFiles.map(f => ({ url: f.fileUrl, name: f.fileName }))) !== trDocsKey(initialTrDocs)) return true;

    // 文档模式切换
    if (momentaReleaseNoteMode !== snap.momentaReleaseNoteMode) return true;
    if (momentaTestReportMode !== snap.momentaTestReportMode) return true;

    // 文档内容变化
    const currRnContent = (form.getFieldValue('momenta_release_note_content') as string) ?? '';
    const currTrContent = (form.getFieldValue('momenta_test_report_content') as string) ?? '';
    if (currRnContent !== snap.momentaReleaseNoteContent) return true;
    if (currTrContent !== snap.momentaTestReportContent) return true;

    // package_ids 变化
    const currIds = ((form.getFieldValue('package_ids') as number[]) ?? []).slice().sort().join(',');
    const snapIds = snap.packageIds.slice().sort().join(',');
    if (currIds !== snapIds) return true;

    // name / 车型 / 子车型 / 硬件平台 / description 变化
    if (((form.getFieldValue('name') as string) ?? '') !== snap.name) return true;
    if (toVehicleList(form.getFieldValue('vehicle_model')).join('\u0000') !== snap.vehicleModel.join('\u0000')) return true;
    if (toVehicleList(form.getFieldValue('vehicle_subtype')).join('\u0000') !== snap.vehicleSubtype.join('\u0000')) return true;
    if (((form.getFieldValue('platform') as string) ?? '') !== snap.platform) return true;
    if (((form.getFieldValue('description') as string) ?? '') !== snap.description) return true;
    if (((form.getFieldValue('version_type') as ExternalReleaseVersionType | undefined) ?? null) !== snap.versionType) return true;

    return false;
  })();

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      onValuesChange={() => { if (isEdit) forceUpdate(n => n + 1); }}
      initialValues={{ name: '', vehicle_model: [], vehicle_subtype: [], platform: '', description: '' }}
    >
      {/* Card 1: 基本信息 */}
      <Card title="基本信息" className="mb-4" styles={{ body: { padding: '12px 16px' } }}>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-x-4 gap-y-2">
          <Form.Item
            name="name"
            label="名称"
            rules={[{ required: true, message: '请输入名称' }]}
            className="mb-0 md:col-span-4"
          >
            <Input placeholder="输入Momenta释放产物包名称" />
          </Form.Item>

          <Form.Item
            name="vehicle_model"
            label="车型"
            tooltip="第三方上传与交付看板仍只使用首个车型"
            className="mb-0 md:col-span-2"
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="选择车型（可多选）"
              options={vehicleTypeOptions}
              onChange={(models: string[]) => {
                // 车型变化后，剔除不再属于所选车型的子车型
                const allowed = new Set(
                  (currentProject?.vehicle_types ?? [])
                    .filter((vt) => models.includes(vt.vehicle_type))
                    .map((vt) => vt.vehicle_subtype)
                    .filter(Boolean),
                );
                const current = toVehicleList(form.getFieldValue('vehicle_subtype'));
                form.setFieldValue(
                  'vehicle_subtype',
                  current.filter((s) => allowed.has(s)),
                );
              }}
            />
          </Form.Item>

          <Form.Item
            name="vehicle_subtype"
            label="子车型"
            className="mb-0 md:col-span-2"
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={(vehicleModelsWatch?.length ?? 0) > 0 ? '选择子车型（可多选）' : '请先选择车型'}
              disabled={!(vehicleModelsWatch?.length ?? 0)}
              options={vehicleSubtypeOptions}
            />
          </Form.Item>

          <Form.Item
            name="platform"
            label="硬件平台"
            className="mb-0 md:col-span-2"
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="选择硬件平台（可选）"
              options={platformOptions}
            />
          </Form.Item>

          <Form.Item
            name="version_type"
            label="版本类型"
            className="mb-0 md:col-span-2"
          >
            <Select
              allowClear
              placeholder="选择版本类型（选填）"
              options={versionTypeOptions}
            />
          </Form.Item>

          <Form.Item
            name="package_ids"
            label="关联底软交付物"
            className="mb-0 md:col-span-4"
          >
            <Select
              mode="multiple"
              placeholder="请选择关联的底软交付物（可多选，选填）"
              allowClear
              optionFilterProp="label"
              options={availablePackages.map((p) => ({ value: p.id, label: p.name }))}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="说明"
            className="mb-0 md:col-span-12"
          >
            <TextArea placeholder="输入说明信息" rows={2} maxLength={500} showCount />
          </Form.Item>
        </div>
      </Card>

      {/* Card 2: Momenta释放文件 */}
      <Card title="Momenta释放文件" className="mb-4" styles={{ body: { padding: '12px 16px' } }}>
        {/* 固定文件 */}
        {momentaFileTypes.length > 0 && (
          <>
            <Divider titlePlacement="start" styles={{ content: { margin: 0 } }} style={{ margin: '8px 0' }}>
              <span style={{ fontWeight: 'bold' }}>固定文件</span>
            </Divider>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {momentaFileTypes.map((fileType) => {
                const stateKey = fileType.code;
                const mode = firmwareModes[stateKey] || 'upload';
                const hasGitData = !!(gitInfo[stateKey]?.url || gitInfo[stateKey]?.branch || gitInfo[stateKey]?.commit);
                const uploadData = uploadedFiles[stateKey];
                const hasUploadData = uploadData
                  ? Array.isArray(uploadData) ? uploadData.length > 0 : true
                  : false;

                const hasArtifactData = (artifactInfo[stateKey] || []).some((v) => v.trim());

                return (
                  <div key={stateKey}>
                    <div className="mb-1">
                      <label className="font-medium block mb-1">
                        {fileType.name}
                        {fileType.description && (
                          <Tooltip title={fileType.description}>
                            <QuestionCircleOutlined style={{ marginLeft: 4, color: '#8c8c8c', fontSize: 12, cursor: 'help' }} />
                          </Tooltip>
                        )}
                      </label>
                      <Radio.Group
                        value={mode}
                        onChange={(e) => {
                          setFirmwareModes((prev) => ({ ...prev, [stateKey]: e.target.value as 'upload' | 'git' | 'artifact' }));
                        }}
                        size="small"
                      >
                        <Radio.Button value="upload" disabled={hasGitData || hasArtifactData}>
                          上传
                        </Radio.Button>
                        <Radio.Button value="git" disabled={hasUploadData || hasArtifactData}>
                          Git
                        </Radio.Button>
                        <Radio.Button value="artifact" disabled={hasUploadData || hasGitData}>
                          Artifact
                        </Radio.Button>
                      </Radio.Group>
                    </div>

                    {mode === 'git' ? (
                      <div className="space-y-2">
                        <Input
                          placeholder="Git仓库URL (必填)"
                          value={gitInfo[stateKey]?.url || ''}
                          onChange={(e) =>
                            setGitInfo((prev) => ({
                              ...prev,
                              [stateKey]: { ...prev[stateKey], url: e.target.value, branch: prev[stateKey]?.branch || '', commit: prev[stateKey]?.commit || '', tag: prev[stateKey]?.tag || '' },
                            }))
                          }
                        />
                        <Input
                          placeholder="Git分支 (必填)"
                          value={gitInfo[stateKey]?.branch || ''}
                          onChange={(e) =>
                            setGitInfo((prev) => ({
                              ...prev,
                              [stateKey]: { ...prev[stateKey], url: prev[stateKey]?.url || '', branch: e.target.value, commit: prev[stateKey]?.commit || '', tag: prev[stateKey]?.tag || '' },
                            }))
                          }
                        />
                        <Input
                          placeholder="Git Commit ID (必填)"
                          value={gitInfo[stateKey]?.commit || ''}
                          onChange={(e) =>
                            setGitInfo((prev) => ({
                              ...prev,
                              [stateKey]: { ...prev[stateKey], url: prev[stateKey]?.url || '', branch: prev[stateKey]?.branch || '', commit: e.target.value, tag: prev[stateKey]?.tag || '' },
                            }))
                          }
                        />
                        <Input
                          placeholder="Git Tag (可选)"
                          value={gitInfo[stateKey]?.tag || ''}
                          onChange={(e) =>
                            setGitInfo((prev) => ({
                              ...prev,
                              [stateKey]: { ...prev[stateKey], url: prev[stateKey]?.url || '', branch: prev[stateKey]?.branch || '', commit: prev[stateKey]?.commit || '', tag: e.target.value },
                            }))
                          }
                        />
                        <Input
                          placeholder="备注（选填）"
                          maxLength={500}
                          allowClear
                          value={remarkInfo[stateKey] ?? ''}
                          onChange={(e) => {
                            setRemarkInfo((prev) => ({ ...prev, [stateKey]: e.target.value }));
                          }}
                        />
                        <Button
                          danger
                          type="link"
                          size="small"
                          onClick={() => handleClearGit(stateKey)}
                          style={{ marginTop: '4px', display: 'block' }}
                        >
                          清空Git配置
                        </Button>
                      </div>
                    ) : mode === 'artifact' ? (
                      <div className="space-y-2">
                        {isMultiFileType(fileType) ? (
                          <>
                            {(artifactInfo[stateKey]?.length ? artifactInfo[stateKey] : ['']).map((name, index) => (
                              <Space.Compact key={index} style={{ width: '100%' }}>
                                <Input
                                  placeholder="Artifact产物包名称 (必填)"
                                  value={name}
                                  onChange={(e) => setArtifactRow(stateKey, index, e.target.value)}
                                />
                                {(artifactInfo[stateKey]?.length || 1) > 1 && (
                                  <Button
                                    icon={<DeleteOutlined />}
                                    onClick={() => removeArtifactRow(stateKey, index)}
                                    aria-label="删除该 Artifact"
                                  />
                                )}
                              </Space.Compact>
                            ))}
                            <Button
                              type="dashed"
                              icon={<PlusOutlined />}
                              onClick={() => addArtifactRow(stateKey)}
                              block
                            >
                              添加 Artifact
                            </Button>
                          </>
                        ) : (
                          <Input
                            placeholder="Artifact产物包名称 (必填)"
                            value={artifactInfo[stateKey]?.[0] || ''}
                            onChange={(e) => setArtifactRow(stateKey, 0, e.target.value)}
                          />
                        )}
                        <Input
                          placeholder="备注（选填）"
                          maxLength={500}
                          allowClear
                          value={remarkInfo[stateKey] ?? ''}
                          onChange={(e) => {
                            setRemarkInfo((prev) => ({ ...prev, [stateKey]: e.target.value }));
                          }}
                        />
                      </div>
                    ) : (
                      isMultiFileType(fileType) ? (
                        <MultiFileUpload
                          fileType={fileType.code}
                          existingFiles={uploadedFiles[stateKey] as UploadedFileWithType[] | undefined}
                          onFilesChange={(files) => {
                            const withId = files.map((f) => ({ ...f, fileTypeId: fileType.id }));
                            setUploadedFiles((prev) => ({ ...prev, [stateKey]: withId.length > 0 ? withId : undefined }));
                          }}
                          uploadConfig={{}}
                          disabled={submitting}
                          deferUpload={true}
                          showRemark={true}
                          allowSelectExisting={true}
                          projectFiles={projectFilesCache[fileType.code] || []}
                          loadingProjectFiles={loadingFiles[fileType.code]}
                          onLoadProjectFiles={() => fetchFilesForType(fileType.code, fileType.id)}
                          onRetry={isEdit ? handleFormRetry : undefined}
                          onDelete={isEdit && initialData?.id ? async (file) => {
                            await deleteExternalReleaseFile(initialData.id, file.fileId!);
                            setUploadedFiles((prev) => {
                              const current = prev[stateKey];
                              if (Array.isArray(current)) {
                                const filtered = (current as UploadedFileWithType[]).filter(f => f.fileId !== file.fileId);
                                return { ...prev, [stateKey]: filtered.length > 0 ? filtered : undefined };
                              }
                              return { ...prev, [stateKey]: undefined };
                            });
                          } : undefined}
                          deferDelete={!!isEdit}
                          onPendingDeleteChange={(pendingFiles) => {
                            setPendingDeleteFiles(prev => {
                              const next = new Map(prev);
                              next.set(stateKey, pendingFiles);
                              return next;
                            });
                          }}
                        />
                      ) : (
                        <MultiFileUpload
                          fileType={fileType.code}
                          existingFiles={
                            uploadedFiles[stateKey]
                              ? Array.isArray(uploadedFiles[stateKey])
                                ? (uploadedFiles[stateKey] as UploadedFileWithType[])
                                : [{ ...(uploadedFiles[stateKey] as UploadedFileWithType), fileTypeId: fileType.id }]
                              : undefined
                          }
                          onFilesChange={(files) => {
                            const withId = files.map((f) => ({ ...f, fileTypeId: fileType.id }));
                            setUploadedFiles((prev) => ({ ...prev, [stateKey]: withId.length > 0 ? withId[0] : undefined }));
                          }}
                          uploadConfig={{}}
                          disabled={submitting}
                          maxFiles={1}
                          deferUpload={true}
                          showRemark={true}
                          allowSelectExisting={true}
                          projectFiles={projectFilesCache[fileType.code] || []}
                          loadingProjectFiles={loadingFiles[fileType.code]}
                          onLoadProjectFiles={() => fetchFilesForType(fileType.code, fileType.id)}
                          onRetry={isEdit ? handleFormRetry : undefined}
                          onDelete={isEdit && initialData?.id ? async (file) => {
                            await deleteExternalReleaseFile(initialData.id, file.fileId!);
                            setUploadedFiles((prev) => ({ ...prev, [stateKey]: undefined }));
                          } : undefined}
                          deferDelete={!!isEdit}
                          onPendingDeleteChange={(pendingFiles) => {
                            setPendingDeleteFiles(prev => {
                              const next = new Map(prev);
                              next.set(stateKey, pendingFiles);
                              return next;
                            });
                          }}
                        />
                      )
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* 文档文件 */}
        <Divider titlePlacement="start" styles={{ content: { margin: 0 } }} style={{ margin: '8px 0' }}>
          <span style={{ fontWeight: 'bold' }}>文档文件</span>
        </Divider>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Momenta Release Note */}
          <div>
            <div className="mb-3">
              <label className="font-medium block mb-2">Momenta Release Note（可选）</label>
              <Radio.Group
                value={momentaReleaseNoteMode}
                onChange={(e) => setMomentaReleaseNoteMode(e.target.value)}
                size="small"
              >
                <Radio.Button value="upload">上传文件</Radio.Button>
                <Radio.Button value="input">输入内容</Radio.Button>
              </Radio.Group>
            </div>

            {/* Existing file in edit mode */}
            {isEdit && existingReleaseNote && momentaReleaseNoteMode === 'upload' && (
              <div className="mb-2 p-2 bg-gray-50 rounded border flex items-center justify-between">
                <span
                  className="text-sm truncate mr-2"
                  style={{
                    color: pendingDeleteReleaseNote ? '#ff4d4f' : undefined,
                    textDecoration: pendingDeleteReleaseNote ? 'line-through' : undefined,
                    opacity: pendingDeleteReleaseNote ? 0.6 : 1,
                  }}
                >
                  {existingReleaseNote.name}
                  {pendingDeleteReleaseNote && <span className="ml-2 text-xs">（待删除）</span>}
                </span>
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={pendingDeleteReleaseNote ? <CheckSquareOutlined /> : <DeleteOutlined />}
                  onClick={handleDeleteReleaseNote}
                />
              </div>
            )}

            {/* 始终挂载，输入模式时隐藏，以保留上传进度和已上传文件状态；有旧文件时需先点删除才显示 */}
            <div style={{ display: (momentaReleaseNoteMode === 'upload' && (!isEdit || !existingReleaseNote || pendingDeleteReleaseNote)) ? undefined : 'none' }}>
              <MultiFileUpload
                fileType="momenta_release_note"
                existingFiles={releaseNoteFile ? [releaseNoteFile] : undefined}
                onFilesChange={(files) => {
                  setReleaseNoteFile(files.length > 0 ? files[0] : undefined);
                }}
                uploadConfig={{}}
                disabled={submitting}
                maxFiles={1}
                deferUpload={true}
              />
              {isEdit && initialData?.momenta_release_note_content && (
                <Form.Item name="momenta_release_note_content" label="文字内容" className="mt-2">
                  <TextArea rows={4} placeholder="输入 Momenta Release Note 内容" />
                </Form.Item>
              )}
            </div>
            {momentaReleaseNoteMode === 'input' && (
              <Form.Item name="momenta_release_note_content">
                <TextArea rows={6} placeholder="输入 Momenta Release Note 内容" />
              </Form.Item>
            )}
          </div>

          {/* Momenta Test Report */}
          <div>
            <div className="mb-3">
              <label className="font-medium block mb-2">Momenta 测试报告（可选）</label>
              <Radio.Group
                value={momentaTestReportMode}
                onChange={(e) => setMomentaTestReportMode(e.target.value)}
                size="small"
              >
                <Radio.Button value="upload">上传文件</Radio.Button>
                <Radio.Button value="input">输入内容</Radio.Button>
              </Radio.Group>
            </div>

            {/* 始终挂载，输入模式时隐藏，以保留上传进度和已上传文件状态 */}
            <div style={{ display: momentaTestReportMode === 'upload' ? undefined : 'none' }}>
              <MultiFileUpload
                fileType="momenta_test_report"
                existingFiles={testReportFiles}
                onFilesChange={(files) => setTestReportFiles(files)}
                uploadConfig={{}}
                disabled={submitting}
                deferUpload={true}
                deferDelete={!!isEdit}
                onPendingDeleteChange={(pendingFiles) => {
                  setPendingDeleteFiles(prev => {
                    const next = new Map(prev);
                    next.set('momenta_test_report', pendingFiles);
                    return next;
                  });
                }}
              />
              {isEdit && initialData?.momenta_test_report_content && (
                <Form.Item name="momenta_test_report_content" label="文字内容" className="mt-2">
                  <TextArea rows={4} placeholder="输入 Momenta 测试报告内容" />
                </Form.Item>
              )}
            </div>
            {momentaTestReportMode === 'input' && (
              <Form.Item name="momenta_test_report_content">
                <TextArea rows={6} placeholder="输入 Momenta 测试报告内容" />
              </Form.Item>
            )}
          </div>
        </div>
      </Card>

      <div className="flex justify-end gap-2">
        <Space>
          <Button icon={<CloseOutlined />} onClick={onCancel}>
            取消
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            htmlType="submit"
            loading={submitting}
            disabled={!hasChanges}
          >
            {isEdit ? '更新' : '提交'}
          </Button>
        </Space>
      </div>
    </Form>
  );
};

export default ExternalReleaseForm;
